import { Box, Center, Stack, Text } from '@chakra-ui/react';
import * as React from 'react';

import {
  GlobeIcon,
  MapPinIcon,
  PlayCircleIcon,
} from '@/imports/ui/chakra/feather';
import { VideoMeetIcon } from '@/imports/ui/chakra/feather/video-meet';

// some of these are used on both the admin and member site
// and some are just used on the admin site. that is why
// some of them have boolean props to show extra information

export const LiveStreamOnTradewingBoothIndicator: React.FC<{
  isLiveVideoStreamDialInEnabled: boolean;
  audienceParticipationCapacity: number;
}> = ({ isLiveVideoStreamDialInEnabled, audienceParticipationCapacity }) => {
  return (
    <Stack direction="row" spacing="4" alignItems="center">
      <Center bg="gray.100" boxSize="12" borderRadius="lg">
        <GlobeIcon fontSize="xl" />
      </Center>
      <Box>
        <Text textStyle="bodyStrong">Live Stream on Tradewing</Text>
        <Text color="gray.600">
          Dial-in {isLiveVideoStreamDialInEnabled ? 'enabled' : 'disabled'},
          max. {audienceParticipationCapacity} participants
        </Text>
      </Box>
    </Stack>
  );
};

export const LiveStreamOnTradewingIndicator: React.FC<
  | {
      showAdminInfo: true;
      isAutomaticallyRecording: boolean;
      isDialInEnabled: boolean;
      audienceParticipationCapacity: number;
    }
  | { showAdminInfo: false }
> = (props) => {
  return (
    <Stack direction="row" spacing="4" alignItems="center">
      <Center bg="gray.100" boxSize="12" borderRadius="lg">
        <GlobeIcon fontSize="xl" />
      </Center>
      <Box>
        <Text textStyle="bodyStrong">Live Stream on Tradewing</Text>
        {props.showAdminInfo && (
          <Text color="gray.600">
            Auto-record{' '}
            {props.isAutomaticallyRecording ? 'enabled' : 'disabled'}, dial-in{' '}
            {props.isDialInEnabled ? 'enabled' : 'disabled'}, max.{' '}
            {props.audienceParticipationCapacity} participants
          </Text>
        )}
      </Box>
    </Stack>
  );
};

export const OnlineConferenceIndicator: React.FC<{
  showAdminInfo: boolean;
}> = ({ showAdminInfo }) => {
  return (
    <Stack>
      <Stack direction="row" spacing="4" alignItems="center">
        <Center bg="gray.100" boxSize="12" borderRadius="lg">
          <GlobeIcon fontSize="xl" />
        </Center>
        <Box>
          <Text textStyle="bodyStrong">Online</Text>
        </Box>
      </Stack>
      {showAdminInfo && (
        <Box p="4" bg="gray.50" borderRadius="lg">
          <Text>
            You will define online settings when creating conference sessions
          </Text>
        </Box>
      )}
    </Stack>
  );
};

export const ExternalPlatformIndicator: React.FC<{
  externalPlatformName: string | null;
}> = ({ externalPlatformName }) => {
  return (
    <Stack direction="row" spacing="4" alignItems="center">
      <Center bg="gray.100" boxSize="12" borderRadius="lg">
        <GlobeIcon fontSize="xl" />
      </Center>
      <Box>
        <Text textStyle="bodyStrong">
          {externalPlatformName
            ? `On ${externalPlatformName}`
            : 'On an External Platform'}
        </Text>
      </Box>
    </Stack>
  );
};

export const PreRecordedVideoIndicator: React.FC<
  | {
      showAdminInfo: true;
      isPreRecordedVideoAutomaticPublishingEnabled: boolean;
    }
  | { showAdminInfo: false }
> = (props) => {
  return (
    <Stack direction="row" spacing="4" alignItems="center">
      <Center bg="gray.100" boxSize="12" borderRadius="lg">
        <PlayCircleIcon fontSize="xl" />
      </Center>
      <Box>
        <Text textStyle="bodyStrong">Pre-Recorded Video on Tradewing</Text>
        {props.showAdminInfo && (
          <Text color="gray.600">
            Guests{' '}
            {props.isPreRecordedVideoAutomaticPublishingEnabled
              ? 'can'
              : 'cannot'}{' '}
            access this video on the event page after the event ends
          </Text>
        )}
      </Box>
    </Stack>
  );
};

export const InPersonIndicator: React.FC<{
  address: {
    administrativeArea: string | null;
    country: string | null;
    locality: string | null;
    name: string | null;
    postalCode: string | null;
    streetAddress: string | null;
    streetAddressLine2: string | null;
  } | null;
}> = ({ address }) => {
  const addressWithoutName = address
    ? [
        address.streetAddress,
        address.streetAddressLine2,
        address.locality,
        address.administrativeArea,
        address.country,
        address.postalCode,
      ]
        .filter((value) => value)
        .join(', ')
    : null;

  return (
    <Stack direction="row" spacing="4" alignItems="center">
      <Center bg="gray.100" boxSize="12" borderRadius="lg">
        <MapPinIcon fontSize="xl" />
      </Center>
      {address ? (
        address.name ? (
          <Box>
            <Text textStyle="bodyStrong">{address.name}</Text>
            {addressWithoutName && (
              <Text color="gray.600">{addressWithoutName}</Text>
            )}
          </Box>
        ) : addressWithoutName ? (
          <Text textStyle="bodyStrong">{addressWithoutName}</Text>
        ) : (
          <Text textStyle="bodyStrong">In Person</Text>
        )
      ) : (
        <Text textStyle="bodyStrong">In Person</Text>
      )}
    </Stack>
  );
};

export const InPersonConferenceSessionOrBoothIndicator: React.FC<{
  description: string | null;
}> = ({ description }) => {
  return (
    <Stack spacing="3">
      <Stack direction="row" spacing="4" alignItems="center">
        <Center bg="gray.100" boxSize="12" borderRadius="lg">
          <MapPinIcon fontSize="xl" />
        </Center>
        {description ? (
          <Text textStyle="bodyStrong">{description}</Text>
        ) : (
          <Text textStyle="bodyStrong">In Person</Text>
        )}
      </Stack>
    </Stack>
  );
};

export const AgoraMeetingOnTradewingIndicator: React.FC<
  | {
      showAdminInfo: true;
      isAutomaticallyRecording: boolean;
      isDialInEnabled: boolean;
     
    }
  | { showAdminInfo: false }
> = (props) => {
  return (
    <Stack direction="row" spacing="4" alignItems="center">
      <Center bg="gray.100" boxSize="12" borderRadius="lg">
        <VideoMeetIcon fontSize="xl" />
      </Center>
      <Box>
        <Text textStyle="bodyStrong">Video Meet on Tradewing</Text>
        {props.showAdminInfo && (
          <Text color="gray.600">
            Auto-record{' '}
            {props.isAutomaticallyRecording ? 'enabled' : 'disabled'}, dial-in{' '}
            {props.isDialInEnabled ? 'enabled' : 'disabled'}
          </Text>
        )}
      </Box>
    </Stack>
  );
};